import { string, object, mixed, array } from 'yup';
import { isValidPhoneNumber } from 'react-phone-number-input';

import type {
  TProgram,
  TProgramWithSubmission,
} from '@src/shared/types/program';
import type { TLead } from '@src/shared/types/lead';
import type { TLabel } from '@src/shared/types/label';
import type { TUser } from '@src/shared/types/user';
import type { TLeadReferal } from '@src/shared/types/lead-referal';

export const createLeadInitialValues = ({
  lead,
  tab = 'single',
}: {
  lead?: TLead;
  tab?: 'single' | 'import';
}): TLeadFormValues => {
  if (tab === 'import') {
    return {
      programs: [] as TProgram[],
      file: null,
    } as TImportLeadFormValues;
  }

  return {
    programs: [] as TProgram[],
    phone_number: '',
    name: '',
    email: '',
    assignee: '' as unknown as TUser,
    referal: '' as unknown as TLeadReferal,
    labels: [] as TLabel[],

    ...createInitialValueFromExistingLead({ lead }),
  } as TSingleLeadFormValues;
};

const createInitialValueFromExistingLead = ({ lead }: { lead?: TLead }) => {
  if (!lead) return {};

  return {
    programs: lead.programs,
    phone_number: lead.phone_number,
    name: lead.name,
    email: lead.email,
    assignee: lead.assignee,
    referal: lead.referal,
    labels: lead.labels,
  };
};

export const reminderFormSchema = object().shape({
  programs: array().of(mixed().required('Program is required')),
  phone_number: string()
    .required('Phone number is required')
    .test(
      'primary_phone_number',
      'Please enter a valid phone number',
      (value) => {
        return isValidPhoneNumber(value);
      }
    ),
  name: string().required('Name is required'),
  email: string().email().required('Email is required'),
  assignee: mixed().required('Assignee is required'),
  referal: mixed().required('Referal is required'),
  labels: array().of(mixed().required('Label is required')),
});

export const leadFormSchema = object({
  programs: array()
    .min(1, 'At least one Program is required')
    .required('Programs are required'),
  name: string().required('Name is required'),
  email: string().email().required('Email is required'),
  phone_number: string()
    .required('Phone is required')
    .test(
      'primary_phone_number',
      'Please enter a valid phone number',
      (value) => {
        return isValidPhoneNumber(value);
      }
    ),
  referal: mixed().nullable(),
  assignee: mixed().nullable(),
});

export const importLeadsFormSchema = object({
  programs: array()
    .min(1, 'At least one Program is required')
    .required('Programs are required'),
  file: mixed()
    .required('File is required')
    .test('file-required', 'File is required', (value) => {
      return value !== null && value !== undefined;
    }),
});

export type TSingleLeadFormValues =
  | {
      programs: TProgram[];
      phone_number: string;
      name: string;
      email: string;
      assignee: TUser;
      referal: TLeadReferal;
      labels: TLabel[];
      file?: undefined;
    }
  | {
      programs: TProgramWithSubmission[];
      phone_number: string;
      name: string;
      email: string;
      assignee: TUser;
      referal: TLeadReferal | undefined;
      labels: TLabel[];
      file?: undefined;
    };

export type TImportLeadFormValues = {
  programs: TProgram[];
  file: File | null;
};

export type TLeadFormValues = TSingleLeadFormValues | TImportLeadFormValues;
